import gql from "graphql-tag";
import type { ApolloClient } from "@apollo/client";

import type {
  PaychexSplitContribution,
  PaychexWorkerRecord,
} from "types/models/paychexWorkerRecord";
import type {
  AcceptInvitationMutation,
  AcceptInvitationMutationVariables,
  AddRolloverAccountDisclosureMutation,
  AddRolloverAccountDisclosureMutationVariables,
  CancelRolloverMutation,
  CancelRolloverMutationVariables,
  CancelUserLinkRequestMutation,
  CancelUserLinkRequestMutationVariables,
  CloseAccountMutation,
  CloseAccountMutationVariables,
  CloseAndLiquidateAccountMutation,
  CloseAndLiquidateAccountMutationVariables,
  CloseAndLiquidateAccountWithDisbursementMutation,
  CloseAndLiquidateAccountWithDisbursementMutationVariables,
  CreateAndLinkSecondaryAuthMutation,
  CreateAndLinkSecondaryAuthMutationVariables,
  CreateAuthLinkingSessionMutation,
  CreateAuthLinkingSessionMutationVariables,
  CreateSupportRequestMutation,
  CreateSupportRequestMutationVariables,
  DeleteEmailChangeRequestMutation,
  DeleteEmailChangeRequestMutationVariables,
  DeleteEmployerLinkMutation,
  DeleteEmployerLinkMutationVariables,
  ExitInvestmentProfileUpdateMutation,
  ExitInvestmentProfileUpdateMutationVariables,
  GetEmailChangeRequestQuery,
  GetEmailChangeRequestQueryVariables,
  GetEmployerLinkRequestsForUserQuery,
  GetEmployerLinkRequestsForUserQueryVariables,
  GetInvestmentProfileAndSuitabilityQuery,
  GetInvestmentProfileAndSuitabilityQueryVariables,
  GetInvestmentProfileQuery,
  GetInvestmentProfileQueryVariables,
  GetIraAccountFormQuery,
  GetIraAccountFormQueryVariables,
  GetNeededDocumentationQuery,
  GetNeededDocumentationQueryVariables,
  GetRequiredUpdatesQuery,
  GetRolloverInfoQuery,
  GetRolloverInfoQueryVariables,
  GetStateQuery,
  GetStateQueryVariables,
  GetUserAgreementsQuery,
  GetUserAgreementsQueryVariables,
  GetUserProfileQuery,
  GetUserProfileQueryVariables,
  GetValidatedAddressQuery,
  GetValidatedAddressQueryVariables,
  InitializePaychexRolloverMutation,
  InitializePaychexRolloverMutationVariables,
  InitializeRolloverMutation,
  InitializeRolloverMutationVariables,
  LinkSecondaryAuthMutation,
  LinkSecondaryAuthMutationVariables,
  ListIndividualLinkedEmployersQuery,
  ListIndividualLinkedEmployersQueryVariables,
  OptOutMutation,
  OptOutMutationVariables,
  RegisterIndividualUserMutation,
  RegisterIndividualUserMutationVariables,
  RegisterMutation,
  RegisterMutationVariables,
  RequestEmailChangeMutation,
  RequestEmailChangeMutationVariables,
  SignupMutation,
  SignupMutationVariables,
  SkipSsnMutation,
  SkipSsnMutationVariables,
  StartInvestmentProfileUpdateMutation,
  StartInvestmentProfileUpdateMutationVariables,
  StartPortfolioUpdateMutation,
  StartPortfolioUpdateMutationVariables,
  SubmitEinMutation,
  SubmitEinMutationVariables,
  SubmitRolloverAccountInfoMutation,
  SubmitRolloverAccountInfoMutationVariables,
  SubmitSsnMutation,
  SubmitSsnMutationVariables,
  SubmitUserRolloverInfoMutation,
  SubmitUserRolloverInfoMutationVariables,
  UpdateEmployerAccountTermsAndSubmitMutation,
  UpdateEmployerAccountTermsAndSubmitMutationVariables,
  UpdateInvestmentProfileAndSuitabilityMutation,
  UpdateInvestmentProfileAndSuitabilityMutationVariables,
  UpdateIraAccountDisclosureMutation,
  UpdateIraAccountDisclosureMutationVariables,
  UpdateIraAccountEmploymentPositionMutation,
  UpdateIraAccountEmploymentPositionMutationVariables,
  UpdateIraAccountEmploymentStatusMutation,
  UpdateIraAccountEmploymentStatusMutationVariables,
  UpdateIraAccountIdentityMutation,
  UpdateIraAccountIdentityMutationVariables,
  UpdateIraAccountInvestmentProfileMutation,
  UpdateIraAccountInvestmentProfileMutationVariables,
  UpdateIraAccountSuitabilityMutation,
  UpdateIraAccountSuitabilityMutationVariables,
  UpdateIraAccountTermsAndSubmitMutation,
  UpdateIraAccountTermsAndSubmitMutationVariables,
  UpdateIraAccountTrustedContactMutation,
  UpdateIraAccountTrustedContactMutationVariables,
  UpdateIraAccountUserCitizenshipMutation,
  UpdateIraAccountUserCitizenshipMutationVariables,
  UpdateIraYellowPathDocumentsMutation,
  UpdateIraYellowPathDocumentsMutationVariables,
  UpdateRequiredDisclosuresMutation,
  UpdateRequiredDisclosuresMutationVariables,
  UpdatePaychexAccountTermsAndSubmitMutation,
  UpdatePaychexAccountTermsAndSubmitMutationVariables,
  UpdateRolloverAccountTermsAndSubmitMutation,
  UpdateRolloverAccountTermsAndSubmitMutationVariables,
  UpdateRolloverInvestmentProfileAndSuitabilityMutation,
  UpdateRolloverInvestmentProfileAndSuitabilityMutationVariables,
  UpdateSelectedAccountTypeMutation,
  UpdateSelectedAccountTypeMutationVariables,
  UpdateUserProfileMutation,
  UpdateUserProfileMutationVariables,
  UserCompletedKycMutation,
  UserCompletedKycMutationVariables,
  UserCompletedOnboardingMutation,
  UserCompletedOnboardingMutationVariables,
  UserEmailConfirmedMutation,
  UserEmailConfirmedMutationVariables,
  UserTermsDateQuery,
  UserTermsDateQueryVariables,
  ValidateEinMutation,
  ValidateEinMutationVariables,
  VerifyEmailChangeMutation,
  VerifyEmailChangeMutationVariables,
} from "types/graphql";

function register(
  client: ApolloClient<object>,
  user: {
    email: string;
    password: string;
    username: string;
    role: string;
    invitationCode?: string | null;
    inviteId?: string | null;
    referralCode?: string | null;
    planCode?: string | null;
    signupCode?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  }
): Promise<SignupMutation> {
  return new Promise((resolve, reject) => {
    // The operation name of Signup is very important for the backend to skip JWT auth so do not change
    const ADD_USER = gql`
      mutation Signup(
        $email: String!
        $password: String!
        $username: String!
        $userRole: String!
        $firstName: String
        $lastName: String
        $invitationCode: String
        $inviteId: String
        $referralCode: String
        $planCode: String
        $signupCode: String
      ) {
        signUp(
          email: $email
          password: $password
          username: $username
          userRole: $userRole
          invitationCode: $invitationCode
          inviteId: $inviteId
          referralCode: $referralCode
          planCode: $planCode
          signupCode: $signupCode
          firstName: $firstName
          lastName: $lastName
        ) {
          id
          error
        }
      }
    `;
    const result = client.mutate<SignupMutation, SignupMutationVariables>({
      mutation: ADD_USER,
      variables: {
        email: user.email,
        password: user.password,
        username: user.username,
        userRole: user.role,
        invitationCode: user.invitationCode,
        inviteId: user.inviteId,
        referralCode: user.referralCode,
        planCode: user.planCode,
        signupCode: user.signupCode,
        firstName: user.firstName,
        lastName: user.lastName,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function registerIndividualUser(
  client: ApolloClient<object>,
  { invitationCode, inviteId }: RegisterIndividualUserMutationVariables
): Promise<RegisterIndividualUserMutation> {
  return new Promise((resolve, reject) => {
    const ADD_USER = gql`
      mutation registerIndividualUser(
        $invitationCode: String
        $inviteId: String
      ) {
        registerIndividualUser(
          invitationCode: $invitationCode
          inviteId: $inviteId
        )
      }
    `;
    const result = client.mutate<
      RegisterIndividualUserMutation,
      RegisterIndividualUserMutationVariables
    >({
      mutation: ADD_USER,
      variables: {
        invitationCode,
        inviteId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function registerAdmin(
  client: ApolloClient<object>,
  user: { email: string; username: string; providerId: string }
): Promise<RegisterMutation> {
  return new Promise((resolve, reject) => {
    const ADD_USER = gql`
      mutation Register(
        $email: String!
        $username: String!
        $providerId: String!
      ) {
        addAdminAccount(
          email: $email
          username: $username
          providerId: $providerId
        ) {
          id
          error
        }
      }
    `;
    const result = client.mutate<RegisterMutation, RegisterMutationVariables>({
      mutation: ADD_USER,
      variables: {
        email: user.email,
        username: user.username,
        providerId: user.providerId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

// user login by email and password
function getState(
  client: ApolloClient<object>
): Promise<GetStateQuery["getState"]> {
  return new Promise((resolve, reject) => {
    const USER_LOGIN = gql`
      query GetState($rid: String!) {
        getState(rid: $rid) {
          user {
            id
            userRoleId
            username
            emailConfirmed
            userAuthorizations {
              id
              identityProvider
              identityProviderId
            }
          }
          userState {
            version
            state
            causedByEvent
            newestVersion
          }
          companyState {
            version
            state
            causedByEvent
            newestVersion
          }
          company {
            id
            name
            dba
            ein
            type
            naicsCode
            size
            url
            planId
            planCode
            enabledProductFeatures
            hasRunFirstPayroll
            referralId
            billingType
            referralPlans {
              min
              max
              planId
              referralCredit
              refereeCredit
            }
            address {
              id
              address1
              address2
              city
              state
              postalCode
              phone
              phoneExt
              fax
            }
            planTypes {
              id
              description
              name
            }
            progressiveOnboardingState
            payrollProvider
            limits {
              key
              value
            }
          }
          featureFlags {
            key
            value
          }
          paychexLinkType
        }
      }
    `;
    const result = client.query<GetStateQuery, GetStateQueryVariables>({
      query: USER_LOGIN,
      variables: { rid: "id-" + Math.random() },
    });
    result.then(
      (data) => {
        resolve(data.data.getState);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function getIraAccountForm(
  client: ApolloClient<object>
): Promise<GetIraAccountFormQuery["getIraAccountForm"]> {
  return new Promise((resolve, reject) => {
    const IRA_ACCOUNT_FORM = gql`
      query GetIraAccountForm {
        getIraAccountForm {
          account {
            type
          }
          identity {
            firstName
            lastName
            legalName
            address1
            address2
            city
            state
            postalCode
            phone
            dob
            ssn
            citizenshipCountry
            permanentResident
            birthCountry
          }
          disclosure {
            tradeAuthorization
            controlPerson
            stockSymbols
            exchangeOrFINRA
            firmName
            politicallyExposed
            organizationName
            familyNames
            docUrl
            isCustodianEmployee
          }
          investmentProfile {
            objective
            experience
            annualIncome {
              min
              max
            }
            liquidNetWorth {
              min
              max
            }
            totalNetWorth {
              min
              max
            }
            riskTolerance
            riskToleranceRawValue
          }
          suitability {
            timeHorizon
            liquidityNeeds
            timeHorizonLiquidity
          }
          employmentStatus {
            status
            employer
            position
          }
          trustedContact {
            trustedContact
            firstName
            lastName
            email
          }
          terms {
            accountAgreement
            termsOfService
          }
        }
      }
    `;
    const result = client.query<
      GetIraAccountFormQuery,
      GetIraAccountFormQueryVariables
    >({
      query: IRA_ACCOUNT_FORM,
    });
    result.then(
      (data) => {
        resolve(data.data.getIraAccountForm);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function updateIraAccountIdentity(
  client: ApolloClient<object>,
  iraAccountData: UpdateIraAccountIdentityMutationVariables
): Promise<UpdateIraAccountIdentityMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_ACCOUNT_IDENTITY = gql`
      mutation UpdateIraAccountIdentity(
        $firstName: String!
        $lastName: String!
        $legalName: String
        $address1: String!
        $address2: String
        $city: String!
        $state: String!
        $postalCode: String!
        $phone: String!
        $dob: String!
        $ssn: String!
      ) {
        updateIraAccountIdentity(
          firstName: $firstName
          lastName: $lastName
          legalName: $legalName
          address1: $address1
          address2: $address2
          city: $city
          state: $state
          postalCode: $postalCode
          phone: $phone
          dob: $dob
          ssn: $ssn
        ) {
          identity {
            firstName
            lastName
            legalName
            address1
            address2
            city
            state
            postalCode
            phone
            dob
            ssn
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateIraAccountIdentityMutation,
      UpdateIraAccountIdentityMutationVariables
    >({
      mutation: UPDATE_ACCOUNT_IDENTITY,
      variables: {
        firstName: iraAccountData.firstName,
        lastName: iraAccountData.lastName,
        legalName: iraAccountData.legalName,
        address1: iraAccountData.address1,
        address2: iraAccountData.address2,
        city: iraAccountData.city,
        state: iraAccountData.state,
        postalCode: iraAccountData.postalCode,
        phone: iraAccountData.phone,
        dob: iraAccountData.dob,
        ssn: iraAccountData.ssn,
      },
    });
    result.then(
      (identityResponse) => {
        if (identityResponse.data) {
          resolve(identityResponse.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(identityResponse);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function updateIraAccountUserCitizenship(
  client: ApolloClient<object>,
  userCitizenship: UpdateIraAccountUserCitizenshipMutationVariables
): Promise<UpdateIraAccountUserCitizenshipMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_USERCITIZENSHIP = gql`
      mutation updateIraAccountUserCitizenship(
        $citizenshipCountry: String!
        $permanentResident: Boolean!
        $birthCountry: String
      ) {
        updateIraAccountUserCitizenship(
          citizenshipCountry: $citizenshipCountry
          permanentResident: $permanentResident
          birthCountry: $birthCountry
        ) {
          identity {
            citizenshipCountry
            permanentResident
            birthCountry
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateIraAccountUserCitizenshipMutation,
      UpdateIraAccountUserCitizenshipMutationVariables
    >({
      mutation: UPDATE_USERCITIZENSHIP,
      variables: {
        citizenshipCountry: userCitizenship.citizenshipCountry,
        permanentResident: userCitizenship.permanentResident,
        birthCountry: userCitizenship.birthCountry,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function updateIraAccountDisclosure(
  client: ApolloClient<object>,
  disclosure: UpdateIraAccountDisclosureMutationVariables
): Promise<UpdateIraAccountDisclosureMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_ACCOUNT_DISCLOSURE = gql`
      mutation UpdateIraAccountDisclosure(
        $tradeAuthorization: Boolean!
        $controlPerson: Boolean!
        $stockSymbols: [String]
        $exchangeOrFINRA: Boolean!
        $firmName: String
        $politicallyExposed: Boolean!
        $organizationName: String
        $familyNames: [String]
        $docUrl: String
        $isCustodianEmployee: Boolean!
      ) {
        updateIraAccountDisclosure(
          tradeAuthorization: $tradeAuthorization
          controlPerson: $controlPerson
          stockSymbols: $stockSymbols
          exchangeOrFINRA: $exchangeOrFINRA
          firmName: $firmName
          politicallyExposed: $politicallyExposed
          organizationName: $organizationName
          familyNames: $familyNames
          docUrl: $docUrl
          isCustodianEmployee: $isCustodianEmployee
        ) {
          disclosure {
            tradeAuthorization
            controlPerson
            stockSymbols
            exchangeOrFINRA
            firmName
            politicallyExposed
            organizationName
            familyNames
            docUrl
            isCustodianEmployee
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateIraAccountDisclosureMutation,
      UpdateIraAccountDisclosureMutationVariables
    >({
      mutation: UPDATE_ACCOUNT_DISCLOSURE,
      variables: {
        tradeAuthorization: disclosure.tradeAuthorization,
        controlPerson: disclosure.controlPerson,
        stockSymbols: disclosure.stockSymbols,
        exchangeOrFINRA: disclosure.exchangeOrFINRA,
        firmName: disclosure.firmName,
        politicallyExposed: disclosure.politicallyExposed,
        organizationName: disclosure.organizationName,
        familyNames: disclosure.familyNames,
        docUrl: disclosure.docUrl,
        isCustodianEmployee: disclosure.isCustodianEmployee,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function updateIraAccountInvestmentProfile(
  client: ApolloClient<object>,
  investmentProfile: UpdateIraAccountInvestmentProfileMutationVariables
): Promise<UpdateIraAccountInvestmentProfileMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_ACCOUNT_INVESTMENT_PROFILE = gql`
      mutation UpdateIraAccountInvestmentProfile(
        $objective: InvestmentObjective!
        $experience: InvestmentExperience!
        $annualIncome: MonetaryRangeInput!
        $liquidNetWorth: MonetaryRangeInput!
        $totalNetWorth: MonetaryRangeInput!
        $riskTolerance: InvestmentRisk!
        $riskToleranceRawValue: String!
        $formMetadata: String!
      ) {
        updateIraAccountInvestmentProfile(
          objective: $objective
          experience: $experience
          annualIncome: $annualIncome
          liquidNetWorth: $liquidNetWorth
          totalNetWorth: $totalNetWorth
          riskTolerance: $riskTolerance
          riskToleranceRawValue: $riskToleranceRawValue
          formMetadata: $formMetadata
        ) {
          investmentProfile {
            objective
            experience
            annualIncome {
              min
              max
              rawValue
            }
            liquidNetWorth {
              min
              max
            }
            totalNetWorth {
              min
              max
            }
            riskTolerance
            formMetadata
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateIraAccountInvestmentProfileMutation,
      UpdateIraAccountInvestmentProfileMutationVariables
    >({
      mutation: UPDATE_ACCOUNT_INVESTMENT_PROFILE,
      variables: {
        objective: investmentProfile.objective,
        experience: investmentProfile.experience,
        annualIncome: investmentProfile.annualIncome,
        liquidNetWorth: investmentProfile.liquidNetWorth,
        totalNetWorth: investmentProfile.totalNetWorth,
        riskTolerance: investmentProfile.riskTolerance,
        riskToleranceRawValue: investmentProfile.riskToleranceRawValue,
        formMetadata: investmentProfile.formMetadata,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function updateIraAccountSuitability(
  client: ApolloClient<object>,
  suitability: UpdateIraAccountSuitabilityMutationVariables
): Promise<UpdateIraAccountSuitabilityMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_ACCOUNT_SUITABILITY = gql`
      mutation UpdateIraAccountSuitability(
        $timeHorizon: TimeHorizon!
        $liquidityNeeds: LiquidityNeeds!
        $timeHorizonLiquidity: String!
      ) {
        updateIraAccountSuitability(
          timeHorizon: $timeHorizon
          liquidityNeeds: $liquidityNeeds
          timeHorizonLiquidity: $timeHorizonLiquidity
        ) {
          suitability {
            timeHorizon
            liquidityNeeds
            timeHorizonLiquidity
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateIraAccountSuitabilityMutation,
      UpdateIraAccountSuitabilityMutationVariables
    >({
      mutation: UPDATE_ACCOUNT_SUITABILITY,
      variables: {
        timeHorizon: suitability.timeHorizon,
        liquidityNeeds: suitability.liquidityNeeds,
        timeHorizonLiquidity: suitability.timeHorizonLiquidity,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function updateIraAccountEmploymentStatus(
  client: ApolloClient<object>,
  employmentStatus: UpdateIraAccountEmploymentStatusMutationVariables
): Promise<UpdateIraAccountEmploymentStatusMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_ACCOUNT_EMPLOYMENTSTATUS = gql`
      mutation UpdateIraAccountEmploymentStatus(
        $status: String!
        $employer: String!
        $position: String!
      ) {
        updateIraAccountEmploymentStatus(
          status: $status
          employer: $employer
          position: $position
        ) {
          employmentStatus {
            status
            employer
            position
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateIraAccountEmploymentStatusMutation,
      UpdateIraAccountEmploymentStatusMutationVariables
    >({
      mutation: UPDATE_ACCOUNT_EMPLOYMENTSTATUS,
      variables: {
        status: employmentStatus.status,
        employer: employmentStatus.employer,
        position: employmentStatus.position,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function updateIraAccountEmploymentPosition(
  client: ApolloClient<object>,
  employmentPosition: UpdateIraAccountEmploymentPositionMutationVariables
): Promise<UpdateIraAccountEmploymentPositionMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_ACCOUNT_EMPLOYMENTPOSITION = gql`
      mutation updateIraAccountEmploymentPosition($position: String!) {
        updateIraAccountEmploymentPosition(position: $position) {
          employmentStatus {
            position
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateIraAccountEmploymentPositionMutation,
      UpdateIraAccountEmploymentPositionMutationVariables
    >({
      mutation: UPDATE_ACCOUNT_EMPLOYMENTPOSITION,
      variables: {
        position: employmentPosition.position,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function updateIraAccountTrustedContact(
  client: ApolloClient<object>,
  trustedContact: UpdateIraAccountTrustedContactMutationVariables
): Promise<UpdateIraAccountTrustedContactMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_ACCOUNT_TRUSTEDCONTACT = gql`
      mutation UpdateIraAccountTrustedContact(
        $trustedContact: Boolean!
        $firstName: String
        $lastName: String
        $email: String
      ) {
        updateIraAccountTrustedContact(
          trustedContact: $trustedContact
          firstName: $firstName
          lastName: $lastName
          email: $email
        ) {
          trustedContact {
            trustedContact
            firstName
            lastName
            email
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateIraAccountTrustedContactMutation,
      UpdateIraAccountTrustedContactMutationVariables
    >({
      mutation: UPDATE_ACCOUNT_TRUSTEDCONTACT,
      variables: {
        trustedContact: trustedContact.trustedContact,
        firstName: trustedContact.firstName,
        lastName: trustedContact.lastName,
        email: trustedContact.email,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function booleanValue(val: unknown) {
  if (val) {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string -- pre-existing coercion semantics for non-boolean inputs
    return val === true || val.toString() === "true";
  }
  return false;
}

function updateIraAccountTermsAndSubmit(
  client: ApolloClient<object>,
  terms: UpdateIraAccountTermsAndSubmitMutationVariables
): Promise<UpdateIraAccountTermsAndSubmitMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_ACCOUNT_TERMS_SUBMIT = gql`
      mutation UpdateIraAccountTermsAndSubmit(
        $termsOfService: Boolean!
        $accountAgreement: Boolean!
      ) {
        updateIraAccountTermsAndSubmit(
          termsOfService: $termsOfService
          accountAgreement: $accountAgreement
        ) {
          terms {
            termsOfService
            accountAgreement
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateIraAccountTermsAndSubmitMutation,
      UpdateIraAccountTermsAndSubmitMutationVariables
    >({
      mutation: UPDATE_ACCOUNT_TERMS_SUBMIT,
      variables: {
        termsOfService: booleanValue(terms.termsOfService),
        accountAgreement: booleanValue(terms.accountAgreement),
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function updateEmployerAccountTermsAndSubmit(
  client: ApolloClient<object>,
  terms: UpdateEmployerAccountTermsAndSubmitMutationVariables
): Promise<UpdateEmployerAccountTermsAndSubmitMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_EMPLOYER_ACCOUNT_TERMS_SUBMIT = gql`
      mutation UpdateEmployerAccountTermsAndSubmit($termsOfService: Boolean!) {
        updateEmployerAccountTermsAndSubmit(termsOfService: $termsOfService)
      }
    `;
    const result = client.mutate<
      UpdateEmployerAccountTermsAndSubmitMutation,
      UpdateEmployerAccountTermsAndSubmitMutationVariables
    >({
      mutation: UPDATE_EMPLOYER_ACCOUNT_TERMS_SUBMIT,
      variables: {
        termsOfService: terms.termsOfService,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

// user logout
function logout(userId: string) {
  // Any cleanup needed to remove local users or on the server
  return new Promise((resolve) => {
    resolve(userId);
  });
}

// confirm email address
function confirmEmail(
  client: ApolloClient<object>
): Promise<UserEmailConfirmedMutation["userEmailConfirmed"] | null> {
  return new Promise((resolve, reject) => {
    const SEND_CONFIRM_EMAIL = gql`
      mutation userEmailConfirmed {
        userEmailConfirmed
      }
    `;
    const result = client.mutate<
      UserEmailConfirmedMutation,
      UserEmailConfirmedMutationVariables
    >({
      mutation: SEND_CONFIRM_EMAIL,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data?.userEmailConfirmed ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getValidatedAddress(
  client: ApolloClient<object>,
  address: GetValidatedAddressQueryVariables
): Promise<GetValidatedAddressQuery["getValidatedAddress"]> {
  return new Promise((resolve, reject) => {
    const GET_VALIDATED_ADDRESS = gql`
      query GetValidatedAddress(
        $address1: String!
        $address2: String
        $city: String!
        $state: String!
        $postalCode: String!
      ) {
        getValidatedAddress(
          address1: $address1
          address2: $address2
          city: $city
          state: $state
          postalCode: $postalCode
        ) {
          address1
          address2
          city
          state
          postalCode
        }
      }
    `;

    const result = client.query<
      GetValidatedAddressQuery,
      GetValidatedAddressQueryVariables
    >({
      query: GET_VALIDATED_ADDRESS,
      variables: address,
    });
    result.then(
      (data) => {
        resolve(data.data.getValidatedAddress);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function userCompletedKyc(
  client: ApolloClient<object>
): Promise<UserCompletedKycMutation["userCompletedKyc"] | null> {
  return new Promise((resolve, reject) => {
    const USER_COMPLETED_KYC = gql`
      mutation userCompletedKyc {
        userCompletedKyc {
          version
          state
          causedByEvent
          newestVersion
        }
      }
    `;
    const result = client.mutate<
      UserCompletedKycMutation,
      UserCompletedKycMutationVariables
    >({
      mutation: USER_COMPLETED_KYC,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data?.userCompletedKyc ?? null);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function userCompletedOnboarding(
  client: ApolloClient<object>
): Promise<UserCompletedOnboardingMutation["userCompletedOnboarding"] | null> {
  return new Promise((resolve, reject) => {
    const USER_COMPLETED_SIGNUP = gql`
      mutation userCompletedOnboarding {
        userCompletedOnboarding {
          version
          state
          causedByEvent
          newestVersion
        }
      }
    `;
    const result = client.mutate<
      UserCompletedOnboardingMutation,
      UserCompletedOnboardingMutationVariables
    >({
      mutation: USER_COMPLETED_SIGNUP,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data?.userCompletedOnboarding ?? null);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function updateIraYellowPathDocuments(
  client: ApolloClient<object>,
  documents: UpdateIraYellowPathDocumentsMutationVariables["documents"]
): Promise<
  UpdateIraYellowPathDocumentsMutation["updateIraYellowPathDocuments"] | null
> {
  return new Promise((resolve, reject) => {
    const UPDATE_IRA_YELLOWPATH_DOCS = gql`
      mutation updateIraYellowPathDocuments($documents: [DocumentInput!]) {
        updateIraYellowPathDocuments(documents: $documents) {
          identity {
            firstName
            lastName
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateIraYellowPathDocumentsMutation,
      UpdateIraYellowPathDocumentsMutationVariables
    >({
      mutation: UPDATE_IRA_YELLOWPATH_DOCS,
      variables: { documents },
    });
    result.then(
      (data) => {
        resolve(data.data?.updateIraYellowPathDocuments ?? null);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function getNeededYellowPathDocs(
  client: ApolloClient<object>
): Promise<GetNeededDocumentationQuery["getNeededDocumentation"]> {
  return new Promise((resolve, reject) => {
    const GET_NEEDED_DOCUMENTS = gql`
      query getNeededDocumentation {
        getNeededDocumentation {
          primaryNeededDocuments
          secondaryNeededDocuments
          tags
        }
      }
    `;
    const result = client.query<
      GetNeededDocumentationQuery,
      GetNeededDocumentationQueryVariables
    >({
      query: GET_NEEDED_DOCUMENTS,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data.getNeededDocumentation);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function getUserProfile(
  client: ApolloClient<object>
): Promise<GetUserProfileQuery["getUserProfile"]> {
  return new Promise((resolve, reject) => {
    const USER_PROFILE = gql`
      query getUserProfile {
        getUserProfile {
          legalName
          firstName
          lastName
          address1
          address2
          city
          state
          postalCode
          phoneNumber
          email
          dob
        }
      }
    `;
    const result = client.query<
      GetUserProfileQuery,
      GetUserProfileQueryVariables
    >({
      query: USER_PROFILE,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data.getUserProfile);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function updateUserProfile(
  client: ApolloClient<object>,
  profileParams: UpdateUserProfileMutationVariables["profileParams"]
): Promise<UpdateUserProfileMutation["updateUserProfile"] | null> {
  return new Promise((resolve, reject) => {
    const UPDATE_USER_PROFILE = gql`
      mutation updateUserProfile($profileParams: UserProfileInput) {
        updateUserProfile(profileParams: $profileParams) {
          legalName
          firstName
          lastName
          address1
          address2
          city
          state
          postalCode
          phoneNumber
          email
        }
      }
    `;
    const result = client.mutate<
      UpdateUserProfileMutation,
      UpdateUserProfileMutationVariables
    >({
      mutation: UPDATE_USER_PROFILE,
      variables: {
        profileParams,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.updateUserProfile ?? null);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function closeUserAccount(
  client: ApolloClient<object>,
  accountId: string,
  reason: string
): Promise<CloseAccountMutation> {
  return new Promise((resolve, reject) => {
    const CLOSE_ACCOUNT = gql`
      mutation closeAccount($accountId: ID!, $reason: String!) {
        closeAccount(accountId: $accountId, reason: $reason)
      }
    `;
    const result = client.mutate<
      CloseAccountMutation,
      CloseAccountMutationVariables
    >({
      mutation: CLOSE_ACCOUNT,
      variables: {
        accountId,
        reason,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function closeAndLiquidateAccountWithDisbursement(
  client: ApolloClient<object>,
  input: CloseAndLiquidateAccountWithDisbursementMutationVariables["input"]
): Promise<CloseAndLiquidateAccountWithDisbursementMutation> {
  return new Promise((resolve, reject) => {
    const CLOSE_ACCOUNT_WITH_FUNDS = gql`
      mutation closeAndLiquidateAccountWithDisbursement(
        $input: CloseAccountWithDisbursementInput!
      ) {
        closeAndLiquidateAccountWithDisbursement(input: $input)
      }
    `;
    const result = client.mutate<
      CloseAndLiquidateAccountWithDisbursementMutation,
      CloseAndLiquidateAccountWithDisbursementMutationVariables
    >({
      mutation: CLOSE_ACCOUNT_WITH_FUNDS,
      variables: {
        input,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function closeAndLiquidateAccount(
  client: ApolloClient<object>,
  { accountId, closeAccountType }: CloseAndLiquidateAccountMutationVariables
): Promise<CloseAndLiquidateAccountMutation> {
  return new Promise((resolve, reject) => {
    const CLOSE_ACCOUNT_WITH_NO_FUNDS = gql`
      mutation closeAndLiquidateAccount(
        $accountId: ID!
        $closeAccountType: String!
      ) {
        closeAndLiquidateAccount(
          accountId: $accountId
          closeAccountType: $closeAccountType
        )
      }
    `;
    const result = client.mutate<
      CloseAndLiquidateAccountMutation,
      CloseAndLiquidateAccountMutationVariables
    >({
      mutation: CLOSE_ACCOUNT_WITH_NO_FUNDS,
      variables: {
        accountId,
        closeAccountType,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function deleteEmailChangeRequest(
  client: ApolloClient<object>,
  emailChangeRequestId: string
): Promise<DeleteEmailChangeRequestMutation> {
  return new Promise((resolve, reject) => {
    const DELETE_EMAIL_CHANGE = gql`
      mutation deleteEmailChangeRequest($emailChangeRequestId: ID!) {
        deleteEmailChangeRequest(emailChangeRequestId: $emailChangeRequestId)
      }
    `;
    const result = client.mutate<
      DeleteEmailChangeRequestMutation,
      DeleteEmailChangeRequestMutationVariables
    >({
      mutation: DELETE_EMAIL_CHANGE,
      variables: {
        emailChangeRequestId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function requestEmailChange(
  client: ApolloClient<object>,
  email: string
): Promise<RequestEmailChangeMutation["requestEmailChange"]> {
  return new Promise((resolve, reject) => {
    const EMAIL_CHANGE = gql`
      mutation requestEmailChange($email: String!) {
        requestEmailChange(email: $email) {
          id
          createdAt
          fromEmail
          toEmail
          userId
        }
      }
    `;
    const result = client.mutate<
      RequestEmailChangeMutation,
      RequestEmailChangeMutationVariables
    >({
      mutation: EMAIL_CHANGE,
      variables: {
        email,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.requestEmailChange);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function verifyEmailChangeRequest(
  client: ApolloClient<object>,
  emailChangeRequestId: string
): Promise<VerifyEmailChangeMutation> {
  return new Promise((resolve, reject) => {
    const VERIFY_EMAIL_CHANGE = gql`
      mutation verifyEmailChange($emailChangeRequestId: ID!) {
        verifyEmailChange(emailChangeRequestId: $emailChangeRequestId) {
          verified
          loginReplaced
        }
      }
    `;
    const result = client.mutate<
      VerifyEmailChangeMutation,
      VerifyEmailChangeMutationVariables
    >({
      mutation: VERIFY_EMAIL_CHANGE,
      variables: {
        emailChangeRequestId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getEmailChangeRequest(
  client: ApolloClient<object>,
  email: string
): Promise<GetEmailChangeRequestQuery["getEmailChangeRequest"]> {
  return new Promise((resolve, reject) => {
    const EMAIL_CHANGE = gql`
      query getEmailChangeRequest($email: String!) {
        getEmailChangeRequest(email: $email) {
          id
          createdAt
          fromEmail
          toEmail
          userId
        }
      }
    `;
    const result = client.query<
      GetEmailChangeRequestQuery,
      GetEmailChangeRequestQueryVariables
    >({
      query: EMAIL_CHANGE,
      variables: {
        email,
      },
    });

    result.then(
      (data) => {
        resolve(data.data.getEmailChangeRequest);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function getInvestmentProfileAndSuitability(
  client: ApolloClient<object>,
  accountId: string
): Promise<GetInvestmentProfileAndSuitabilityQuery> {
  return new Promise((resolve, reject) => {
    const GET_INVESTMENT_PROFILE_AND_SUITABILITY = gql`
      query getInvestmentProfileAndSuitability($accountId: ID) {
        getInvestmentProfile(accountId: $accountId) {
          objective
          experience
          annualIncome {
            min
            max
            rawValue
          }
          liquidNetWorth {
            min
            max
          }
          totalNetWorth {
            min
            max
          }
          riskTolerance
          riskToleranceRawValue
          formMetadata
        }
        getSuitability(accountId: $accountId) {
          timeHorizon
          liquidityNeeds
          timeHorizonLiquidity
        }
      }
    `;
    const result = client.query<
      GetInvestmentProfileAndSuitabilityQuery,
      GetInvestmentProfileAndSuitabilityQueryVariables
    >({
      query: GET_INVESTMENT_PROFILE_AND_SUITABILITY,
      variables: {
        accountId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getInvestmentProfile(
  client: ApolloClient<object>
): Promise<GetInvestmentProfileQuery["getInvestmentProfile"]> {
  return new Promise((resolve, reject) => {
    const GET_INVESTMENT_PROFILE = gql`
      query getInvestmentProfile {
        getInvestmentProfile {
          objective
          experience
          annualIncome {
            min
            max
            rawValue
          }
          liquidNetWorth {
            min
            max
          }
          totalNetWorth {
            min
            max
          }
          riskTolerance
          riskToleranceRawValue
          formMetadata
        }
      }
    `;
    const result = client.query<
      GetInvestmentProfileQuery,
      GetInvestmentProfileQueryVariables
    >({
      query: GET_INVESTMENT_PROFILE,
      variables: {},
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getInvestmentProfile);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function updateInvestmentProfileAndSuitability(
  client: ApolloClient<object>,
  accountId: string,
  investmentProfile: UpdateInvestmentProfileAndSuitabilityMutationVariables["investmentProfile"],
  suitability: UpdateInvestmentProfileAndSuitabilityMutationVariables["suitability"],
  isAccountScoped: boolean
): Promise<UpdateInvestmentProfileAndSuitabilityMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_INVESTMENT_PROFILE_AND_SUITABILITY = gql`
      mutation updateInvestmentProfileAndSuitability(
        $accountId: ID
        $investmentProfile: IraAccountInvestmentProfileInput!
        $suitability: IraAccountSuitabilityInput!
        $isAccountScoped: Boolean!
      ) {
        updateInvestmentProfileAndSuitability(
          accountId: $accountId
          input: {
            investmentProfile: $investmentProfile
            suitability: $suitability
          }
          isAccountScoped: $isAccountScoped
        ) {
          investmentProfile {
            objective
            experience
            annualIncome {
              min
              max
            }
            liquidNetWorth {
              min
              max
            }
            totalNetWorth {
              min
              max
            }
            riskTolerance
            riskToleranceRawValue
            formMetadata
          }
          suitability {
            timeHorizon
            liquidityNeeds
            timeHorizonLiquidity
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateInvestmentProfileAndSuitabilityMutation,
      UpdateInvestmentProfileAndSuitabilityMutationVariables
    >({
      mutation: UPDATE_INVESTMENT_PROFILE_AND_SUITABILITY,
      variables: {
        investmentProfile,
        suitability,
        accountId,
        isAccountScoped,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function acceptInvitation(
  client: ApolloClient<object>,
  invitationCode: string
): Promise<AcceptInvitationMutation["acceptInvitation"] | null> {
  return new Promise((resolve, reject) => {
    const ACCEPT_INVITATION = gql`
      mutation acceptInvitation($invitationCode: String!) {
        acceptInvitation(invitationCode: $invitationCode) {
          id
          status
          company {
            id
            name
            planId
            enabledProductFeatures
          }
          group {
            id
            payrollFrequency
            companyGroupIndex
            useUniqueInviteCodes
          }
        }
      }
    `;
    const result = client.mutate<
      AcceptInvitationMutation,
      AcceptInvitationMutationVariables
    >({
      mutation: ACCEPT_INVITATION,
      variables: {
        invitationCode,
      },
    });
    result.then(
      (response) => {
        if (response.data?.acceptInvitation) {
          resolve(response.data?.acceptInvitation ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function optOut(
  client: ApolloClient<object>,
  inviteId: string,
  optedOut: boolean
): Promise<OptOutMutation["optOut"] | null> {
  return new Promise((resolve, reject) => {
    const OPT_OUT = gql`
      mutation optOut($inviteId: String!, $optedOut: Boolean!) {
        optOut(inviteId: $inviteId, optedOut: $optedOut) {
          optedOut
        }
      }
    `;
    const result = client.mutate<OptOutMutation, OptOutMutationVariables>({
      mutation: OPT_OUT,
      variables: {
        inviteId,
        optedOut,
      },
    });
    result.then(
      (response) => {
        if (response.data?.optOut) {
          resolve(response.data?.optOut ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getEmployerLinkRequests(
  client: ApolloClient<object>
): Promise<
  GetEmployerLinkRequestsForUserQuery["getEmployerLinkRequestsForUser"]
> {
  return new Promise((resolve, reject) => {
    const GET_LINK_REQUESTS = gql`
      query getEmployerLinkRequestsForUser {
        getEmployerLinkRequestsForUser {
          id
          status
          company {
            id
            name
            planId
            enabledProductFeatures
          }
          group {
            id
            payrollFrequency
            companyGroupIndex
            useUniqueInviteCodes
          }
          schedule {
            start
            end
            payrollProcessingDate
            onOrAfterPayrollProcessingDate
            status
          }
          contribution {
            amount
            id
            accountId
            contributionChangeRequests {
              userCurrentContributionAmount
              userNewContributionAmount
              status
              id
            }
          }
        }
      }
    `;
    const result = client.query<
      GetEmployerLinkRequestsForUserQuery,
      GetEmployerLinkRequestsForUserQueryVariables
    >({
      query: GET_LINK_REQUESTS,
      variables: {},
      fetchPolicy: "network-only",
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getEmployerLinkRequestsForUser);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getLinkedEmployers(
  client: ApolloClient<object>
): Promise<
  ListIndividualLinkedEmployersQuery["listIndividualLinkedEmployers"]
> {
  return new Promise((resolve, reject) => {
    const GET_LINKS = gql`
      query listIndividualLinkedEmployers {
        listIndividualLinkedEmployers {
          id
          company {
            id
            name
            planId
          }
          groupWithSchedule {
            group {
              id
              name
              payrollFrequency
              companyGroupIndex
            }
          }
          group {
            id
            name
            payrollFrequency
            companyGroupIndex
          }

          schedule {
            start
            end
            payrollProcessingDate
            onOrAfterPayrollProcessingDate
            status
          }
          contribution {
            amount
            id
            accountId
            contributionChangeRequests {
              status
              id
              userCurrentContributionAmount
              userNewContributionAmount
            }
          }
        }
      }
    `;
    const result = client.query<
      ListIndividualLinkedEmployersQuery,
      ListIndividualLinkedEmployersQueryVariables
    >({
      query: GET_LINKS,
      variables: {},
      fetchPolicy: "network-only",
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.listIndividualLinkedEmployers);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function deleteEmployerLink(
  client: ApolloClient<object>,
  linkId: string
): Promise<DeleteEmployerLinkMutation["deleteEmployerLink"] | null> {
  return new Promise((resolve, reject) => {
    const DELETE_LINK = gql`
      mutation deleteEmployerLink($linkId: ID!) {
        deleteEmployerLink(linkId: $linkId)
      }
    `;
    const result = client.mutate<
      DeleteEmployerLinkMutation,
      DeleteEmployerLinkMutationVariables
    >({
      mutation: DELETE_LINK,
      variables: {
        linkId,
      },
    });
    result.then(
      (response) => {
        if (response.data?.deleteEmployerLink) {
          resolve(response.data?.deleteEmployerLink ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function deleteEmployerLinkRequest(
  client: ApolloClient<object>,
  linkRequestId: string
): Promise<CancelUserLinkRequestMutation["cancelUserLinkRequest"] | null> {
  return new Promise((resolve, reject) => {
    const DELETE_LINK = gql`
      mutation cancelUserLinkRequest($linkRequestId: ID!) {
        cancelUserLinkRequest(linkRequestId: $linkRequestId) {
          id
          status
          company {
            id
            name
            planId
          }
          group {
            id
            payrollFrequency
            companyGroupIndex
          }
          schedule {
            start
            end
            payrollProcessingDate
            onOrAfterPayrollProcessingDate
            status
          }
          contribution {
            amount
            id
            contributionChangeRequests {
              userCurrentContributionAmount
              userNewContributionAmount
              status
              id
            }
          }
        }
      }
    `;
    const result = client.mutate<
      CancelUserLinkRequestMutation,
      CancelUserLinkRequestMutationVariables
    >({
      mutation: DELETE_LINK,
      variables: {
        linkRequestId,
      },
    });
    result.then(
      (response) => {
        if (response.data?.cancelUserLinkRequest) {
          resolve(response.data?.cancelUserLinkRequest ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function startInvestmentProfileUpdateRequest(
  client: ApolloClient<object>
): Promise<
  StartInvestmentProfileUpdateMutation["startInvestmentProfileUpdate"] | null
> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation startInvestmentProfileUpdate {
        startInvestmentProfileUpdate
      }
    `;
    const result = client.mutate<
      StartInvestmentProfileUpdateMutation,
      StartInvestmentProfileUpdateMutationVariables
    >({
      mutation: REQUEST,
    });
    result.then(
      (response) => {
        if (response.data?.startInvestmentProfileUpdate) {
          resolve(response.data?.startInvestmentProfileUpdate ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function startPortfolioUpdateRequest(
  client: ApolloClient<object>
): Promise<StartPortfolioUpdateMutation["startPortfolioUpdate"] | null> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation startPortfolioUpdate {
        startPortfolioUpdate
      }
    `;
    const result = client.mutate<
      StartPortfolioUpdateMutation,
      StartPortfolioUpdateMutationVariables
    >({
      mutation: REQUEST,
    });
    result.then(
      (response) => {
        if (response.data?.startPortfolioUpdate) {
          resolve(response.data?.startPortfolioUpdate ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function exitInvestmentProfileUpdateRequest(
  client: ApolloClient<object>
): Promise<
  ExitInvestmentProfileUpdateMutation["exitInvestmentProfileUpdate"] | null
> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation exitInvestmentProfileUpdate {
        exitInvestmentProfileUpdate
      }
    `;
    const result = client.mutate<
      ExitInvestmentProfileUpdateMutation,
      ExitInvestmentProfileUpdateMutationVariables
    >({
      mutation: REQUEST,
    });
    result.then(
      (response) => {
        if (response.data?.exitInvestmentProfileUpdate) {
          resolve(response.data?.exitInvestmentProfileUpdate ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function skipSsn(
  client: ApolloClient<object>
): Promise<SkipSsnMutation["skipSsn"] | null> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation skipSsn {
        skipSsn
      }
    `;
    const result = client.mutate<SkipSsnMutation, SkipSsnMutationVariables>({
      mutation: REQUEST,
    });
    result.then(
      (response) => {
        if (response.data?.skipSsn) {
          resolve(response.data?.skipSsn ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function submitSsn(
  client: ApolloClient<object>,
  ssn: string
): Promise<SubmitSsnMutation["submitSsn"] | null> {
  return new Promise((resolve, reject) => {
    const SUBMIT_SSN = gql`
      mutation submitSsn($ssn: String!) {
        submitSsn(ssn: $ssn)
      }
    `;
    const result = client.mutate<SubmitSsnMutation, SubmitSsnMutationVariables>(
      {
        mutation: SUBMIT_SSN,
        variables: {
          ssn,
        },
      }
    );
    result.then(
      (response) => {
        if (response.data?.submitSsn) {
          resolve(response.data?.submitSsn ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function submitEin(
  client: ApolloClient<object>,
  { ein, companyId }: SubmitEinMutationVariables
): Promise<SubmitEinMutation["submitEin"] | null> {
  return new Promise((resolve, reject) => {
    const SUBMIT_EIN = gql`
      mutation submitEin($ein: String!, $companyId: ID!) {
        submitEin(ein: $ein, companyId: $companyId)
      }
    `;
    const result = client.mutate<SubmitEinMutation, SubmitEinMutationVariables>(
      {
        mutation: SUBMIT_EIN,
        variables: {
          ein,
          companyId,
        },
      }
    );
    result.then(
      (response) => {
        if (response.data?.submitEin) {
          resolve(response.data?.submitEin ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function validateEin(
  client: ApolloClient<object>,
  { ein, companyId }: ValidateEinMutationVariables
): Promise<ValidateEinMutation["validateEin"] | null> {
  return new Promise((resolve, reject) => {
    const VALIDATE_EIN = gql`
      mutation validateEin($ein: String!, $companyId: ID!) {
        validateEin(ein: $ein, companyId: $companyId)
      }
    `;
    const result = client.mutate<
      ValidateEinMutation,
      ValidateEinMutationVariables
    >({
      mutation: VALIDATE_EIN,
      variables: {
        ein,
        companyId,
      },
    });
    result.then(
      (response) => {
        if (response.data?.validateEin) {
          resolve(response.data?.validateEin ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function createSupportRequest(
  client: ApolloClient<object>,
  { comment, subject, email, name }: CreateSupportRequestMutationVariables
): Promise<CreateSupportRequestMutation["createSupportRequest"] | null> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation createSupportRequest(
        $comment: String!
        $subject: String!
        $email: String!
        $name: String!
      ) {
        createSupportRequest(
          comment: $comment
          subject: $subject
          email: $email
          name: $name
        )
      }
    `;
    const result = client.mutate<
      CreateSupportRequestMutation,
      CreateSupportRequestMutationVariables
    >({
      mutation: REQUEST,
      variables: {
        comment,
        subject,
        email,
        name,
      },
    });
    result.then(
      (response) => {
        if (response.data?.createSupportRequest) {
          resolve(response.data?.createSupportRequest ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getRequiredStepsRequest(
  client: ApolloClient<object>
): Promise<GetRequiredUpdatesQuery["getRequiredUpdates"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getRequiredUpdates {
        getRequiredUpdates {
          updates {
            id
            entityIdentifier {
              type
              id
            }
            allSteps
            pendingSteps
            isCompleted
            accountType
          }
        }
      }
    `;

    client
      .query<GetRequiredUpdatesQuery>({
        query: REQUEST,
        fetchPolicy: "network-only",
      })
      .then((response) => {
        if (response.data && response.data.getRequiredUpdates) {
          resolve(response.data.getRequiredUpdates);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

function updateRequiredDisclosuresRequest(
  client: ApolloClient<object>,
  { isCustodianEmployee }: UpdateRequiredDisclosuresMutationVariables
): Promise<
  UpdateRequiredDisclosuresMutation["updateRequiredDisclosures"] | null
> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation updateRequiredDisclosures($isCustodianEmployee: Boolean!) {
        updateRequiredDisclosures(isCustodianEmployee: $isCustodianEmployee) {
          id
          entityIdentifier {
            type
            id
          }
          isCompleted
          allSteps
          pendingSteps
        }
      }
    `;
    const result = client.mutate<
      UpdateRequiredDisclosuresMutation,
      UpdateRequiredDisclosuresMutationVariables
    >({
      mutation: REQUEST,
      variables: {
        isCustodianEmployee,
      },
    });
    result.then(
      (response) => {
        if (response.data?.updateRequiredDisclosures) {
          resolve(response.data?.updateRequiredDisclosures ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function updateSelectedAccountType(
  client: ApolloClient<object>,
  accountType: UpdateSelectedAccountTypeMutationVariables["accountType"]
): Promise<UpdateSelectedAccountTypeMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_SELECTED_ACCOUNT_TYPE = gql`
      mutation updateSelectedAccountType($accountType: AccountType!) {
        updateSelectedAccountType(accountType: $accountType)
      }
    `;
    const result = client.mutate<
      UpdateSelectedAccountTypeMutation,
      UpdateSelectedAccountTypeMutationVariables
    >({
      mutation: UPDATE_SELECTED_ACCOUNT_TYPE,
      variables: {
        accountType,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function initializeRollover(
  client: ApolloClient<object>,
  accountType: InitializeRolloverMutationVariables["accountType"]
): Promise<InitializeRolloverMutation> {
  return new Promise((resolve, reject) => {
    const INITIALIZE_ROLLOVER = gql`
      mutation initializeRollover($accountType: RolloverAccountType!) {
        initializeRollover(accountType: $accountType) {
          id
          routingNumber
          accountNumber
          name
          accountType
          isRollover
          isProcessingRollover
          state
          createdAt
          active
          lifecycleState
          balances {
            withdrawalBalance
            totalAccountBalance
            cashBalance
            principal
            totalPendingContributions
            totalPendingDistributions
          }
        }
      }
    `;
    const result = client.mutate<
      InitializeRolloverMutation,
      InitializeRolloverMutationVariables
    >({
      mutation: INITIALIZE_ROLLOVER,
      variables: {
        accountType,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function initializePaychexRollover(
  client: ApolloClient<object>,
  accountType: InitializePaychexRolloverMutationVariables["accountType"]
): Promise<InitializePaychexRolloverMutation> {
  return new Promise((resolve, reject) => {
    const INITIALIZE_PAYCHEX_ROLLOVER = gql`
      mutation initializePaychexRollover(
        $accountType: PaychexRolloverAccountType!
      ) {
        initializePaychexRollover(accountType: $accountType) {
          id
          routingNumber
          accountNumber
          name
          accountType
          isRollover
          isProcessingRollover
          state
          createdAt
          active
          lifecycleState
          balances {
            withdrawalBalance
            totalAccountBalance
            cashBalance
            principal
            totalPendingContributions
            totalPendingDistributions
          }
        }
      }
    `;
    const result = client.mutate<
      InitializePaychexRolloverMutation,
      InitializePaychexRolloverMutationVariables
    >({
      mutation: INITIALIZE_PAYCHEX_ROLLOVER,
      variables: {
        accountType,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function submitRolloverAccountInfo(
  client: ApolloClient<object>,
  input: SubmitRolloverAccountInfoMutationVariables["input"]
): Promise<SubmitRolloverAccountInfoMutation> {
  return new Promise((resolve, reject) => {
    const SUBMIT_ROLLOVER_INFO = gql`
      mutation submitRolloverAccountInfo($input: [RolloverAccountInput!]!) {
        submitRolloverAccountInfo(input: $input) {
          id
          routingNumber
          accountNumber
          name
          accountType
          isRollover
          isProcessingRollover
          state
          createdAt
          active
          lifecycleState
          balances {
            withdrawalBalance
            totalAccountBalance
            cashBalance
            principal
            totalPendingContributions
            totalPendingDistributions
          }
        }
      }
    `;

    const result = client.mutate<
      SubmitRolloverAccountInfoMutation,
      SubmitRolloverAccountInfoMutationVariables
    >({
      mutation: SUBMIT_ROLLOVER_INFO,
      variables: { input },
    });

    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function submitUserRolloverInfo(
  client: ApolloClient<object>,
  input: SubmitUserRolloverInfoMutationVariables["input"]
): Promise<SubmitUserRolloverInfoMutation> {
  return new Promise((resolve, reject) => {
    const SUBMIT_USER_ROLLOVER_INFO = gql`
      mutation submitUserRolloverInfo($input: [RolloverAccountInput!]!) {
        submitUserRolloverInfo(input: $input) {
          id
          routingNumber
          accountNumber
          name
          accountType
          isRollover
          isProcessingRollover
          state
          createdAt
          active
          lifecycleState
          balances {
            withdrawalBalance
            totalAccountBalance
            cashBalance
            principal
            totalPendingContributions
            totalPendingDistributions
          }
        }
      }
    `;

    const result = client.mutate<
      SubmitUserRolloverInfoMutation,
      SubmitUserRolloverInfoMutationVariables
    >({
      mutation: SUBMIT_USER_ROLLOVER_INFO,
      variables: { input },
    });

    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function addRolloverAccountDisclosure(
  client: ApolloClient<object>,
  disclosure: AddRolloverAccountDisclosureMutationVariables
): Promise<AddRolloverAccountDisclosureMutation> {
  return new Promise((resolve, reject) => {
    const ADD_ROLLOVER_ACCOUNT_DISCLOSURE = gql`
      mutation addRolloverAccountDisclosure(
        $tradeAuthorization: Boolean!
        $controlPerson: Boolean!
        $stockSymbols: [String]
        $exchangeOrFINRA: Boolean!
        $firmName: String
        $politicallyExposed: Boolean!
        $organizationName: String
        $familyNames: [String]
        $docUrl: String
        $isCustodianEmployee: Boolean!
      ) {
        addRolloverAccountDisclosure(
          tradeAuthorization: $tradeAuthorization
          controlPerson: $controlPerson
          stockSymbols: $stockSymbols
          exchangeOrFINRA: $exchangeOrFINRA
          firmName: $firmName
          politicallyExposed: $politicallyExposed
          organizationName: $organizationName
          familyNames: $familyNames
          docUrl: $docUrl
          isCustodianEmployee: $isCustodianEmployee
        ) {
          id
          routingNumber
          accountNumber
          name
          accountType
          isRollover
          isProcessingRollover
          state
          createdAt
          active
          lifecycleState
          balances {
            withdrawalBalance
            totalAccountBalance
            cashBalance
            principal
            totalPendingContributions
            totalPendingDistributions
          }
        }
      }
    `;

    const result = client.mutate<
      AddRolloverAccountDisclosureMutation,
      AddRolloverAccountDisclosureMutationVariables
    >({
      mutation: ADD_ROLLOVER_ACCOUNT_DISCLOSURE,
      variables: {
        tradeAuthorization: disclosure.tradeAuthorization,
        controlPerson: disclosure.controlPerson,
        stockSymbols: disclosure.stockSymbols,
        exchangeOrFINRA: disclosure.exchangeOrFINRA,
        firmName: disclosure.firmName,
        politicallyExposed: disclosure.politicallyExposed,
        organizationName: disclosure.organizationName,
        familyNames: disclosure.familyNames,
        docUrl: disclosure.docUrl,
        isCustodianEmployee: disclosure.isCustodianEmployee,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function updateRolloverInvestmentProfileAndSuitability(
  client: ApolloClient<object>,
  investmentProfile: UpdateRolloverInvestmentProfileAndSuitabilityMutationVariables["investmentProfile"],
  suitability: UpdateRolloverInvestmentProfileAndSuitabilityMutationVariables["suitability"]
): Promise<UpdateRolloverInvestmentProfileAndSuitabilityMutation> {
  return new Promise((resolve, reject) => {
    const USER_ROLLOVER_INVESTMENT_PROFILE_UPDATE = gql`
      mutation UpdateRolloverInvestmentProfileAndSuitability(
        $investmentProfile: IraAccountInvestmentProfileInput!
        $suitability: IraAccountSuitabilityInput!
      ) {
        updateRolloverInvestmentProfileAndSuitability(
          input: {
            investmentProfile: $investmentProfile
            suitability: $suitability
          }
        ) {
          id
          routingNumber
          accountNumber
          name
          accountType
          isRollover
          isProcessingRollover
          state
          createdAt
          active
          lifecycleState
          balances {
            withdrawalBalance
            totalAccountBalance
            cashBalance
            principal
            totalPendingContributions
            totalPendingDistributions
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateRolloverInvestmentProfileAndSuitabilityMutation,
      UpdateRolloverInvestmentProfileAndSuitabilityMutationVariables
    >({
      mutation: USER_ROLLOVER_INVESTMENT_PROFILE_UPDATE,
      variables: {
        investmentProfile,
        suitability,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function updateRolloverAccountTermsAndSubmit(
  client: ApolloClient<object>,
  terms: UpdateRolloverAccountTermsAndSubmitMutationVariables
): Promise<UpdateRolloverAccountTermsAndSubmitMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_ROLLOVER_ACCOUNT_TERMS_SUBMIT = gql`
      mutation updateRolloverAccountTermsAndSubmit(
        $termsOfService: Boolean!
        $accountAgreement: Boolean!
        $rolloverTerms: Boolean!
      ) {
        updateRolloverAccountTermsAndSubmit(
          termsOfService: $termsOfService
          accountAgreement: $accountAgreement
          rolloverTerms: $rolloverTerms
        ) {
          id
          routingNumber
          accountNumber
          name
          accountType
          isRollover
          isProcessingRollover
          state
          createdAt
          active
          lifecycleState
          balances {
            withdrawalBalance
            totalAccountBalance
            cashBalance
            principal
            totalPendingContributions
            totalPendingDistributions
          }
        }
      }
    `;
    const result = client.mutate<
      UpdateRolloverAccountTermsAndSubmitMutation,
      UpdateRolloverAccountTermsAndSubmitMutationVariables
    >({
      mutation: UPDATE_ROLLOVER_ACCOUNT_TERMS_SUBMIT,
      variables: {
        termsOfService: booleanValue(terms.termsOfService),
        accountAgreement: booleanValue(terms.accountAgreement),
        rolloverTerms: booleanValue(terms.rolloverTerms),
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function updatePaychexAccountTermsAndSubmit(
  client: ApolloClient<object>,
  terms: UpdatePaychexAccountTermsAndSubmitMutationVariables
): Promise<UpdatePaychexAccountTermsAndSubmitMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_PAYCHEX_ACCOUNT_TERMS_SUBMIT = gql`
      mutation updatePaychexAccountTermsAndSubmit(
        $termsOfService: Boolean!
        $accountAgreement: Boolean!
      ) {
        updatePaychexAccountTermsAndSubmit(
          termsOfService: $termsOfService
          accountAgreement: $accountAgreement
        ) {
          id
          routingNumber
          accountNumber
          name
          accountType
          isRollover
          isProcessingRollover
          state
          createdAt
          active
          lifecycleState
          balances {
            withdrawalBalance
            totalAccountBalance
            cashBalance
            principal
            totalPendingContributions
            totalPendingDistributions
          }
        }
      }
    `;
    const result = client.mutate<
      UpdatePaychexAccountTermsAndSubmitMutation,
      UpdatePaychexAccountTermsAndSubmitMutationVariables
    >({
      mutation: UPDATE_PAYCHEX_ACCOUNT_TERMS_SUBMIT,
      variables: {
        termsOfService: booleanValue(terms.termsOfService),
        accountAgreement: booleanValue(terms.accountAgreement),
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function userTermsDate(
  client: ApolloClient<object>
): Promise<UserTermsDateQuery["userTermsDate"]> {
  return new Promise((resolve, reject) => {
    const QUERY = gql`
      query userTermsDate {
        userTermsDate {
          createdAt
        }
      }
    `;
    const result = client.query<
      UserTermsDateQuery,
      UserTermsDateQueryVariables
    >({
      query: QUERY,
      variables: {},
    });
    result.then(
      (response) => {
        if (response.data?.userTermsDate) {
          resolve(response.data.userTermsDate);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function getRolloverInfo(
  client: ApolloClient<object>,
  accountId: string
): Promise<GetRolloverInfoQuery["getRolloverInfo"]> {
  return new Promise((resolve, reject) => {
    const GET_ROLLOVER_INFO = gql`
      query getRolloverInfo($accountId: ID!) {
        getRolloverInfo(accountId: $accountId) {
          amount
          status
          accountId
          docId
          accountNumber
          financialInstitution
        }
      }
    `;
    const result = client.query<
      GetRolloverInfoQuery,
      GetRolloverInfoQueryVariables
    >({
      query: GET_ROLLOVER_INFO,
      variables: {
        accountId,
      },
    });
    result.then(
      (data) => {
        resolve(data.data.getRolloverInfo);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

function cancelRollover(
  client: ApolloClient<object>,
  accountId: string
): Promise<CancelRolloverMutation> {
  return new Promise((resolve, reject) => {
    const CANCEL_ROLLOVER = gql`
      mutation cancelRollover($accountId: ID!) {
        cancelRollover(accountId: $accountId)
      }
    `;

    const result = client.mutate<
      CancelRolloverMutation,
      CancelRolloverMutationVariables
    >({
      mutation: CANCEL_ROLLOVER,
      variables: { accountId },
    });

    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function linkSecondaryAuth(
  client: ApolloClient<object>,
  authLinkingSessionId: string
): Promise<LinkSecondaryAuthMutation> {
  return new Promise((resolve, reject) => {
    const MUTATION = gql`
      mutation linkSecondaryAuth($authLinkingSessionId: ID!) {
        linkSecondaryAuth(authLinkingSessionId: $authLinkingSessionId) {
          id
          identityProvider
          identityProviderId
        }
      }
    `;

    const result = client.mutate<
      LinkSecondaryAuthMutation,
      LinkSecondaryAuthMutationVariables
    >({
      mutation: MUTATION,
      variables: { authLinkingSessionId },
    });

    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function createAndLinkSecondaryAuth(
  client: ApolloClient<object>,
  { email, username, password }: CreateAndLinkSecondaryAuthMutationVariables
): Promise<CreateAndLinkSecondaryAuthMutation> {
  return new Promise((resolve, reject) => {
    const MUTATION = gql`
      mutation createAndLinkSecondaryAuth(
        $email: String!
        $username: String!
        $password: String!
      ) {
        createAndLinkSecondaryAuth(
          email: $email
          username: $username
          password: $password
        ) {
          id
          identityProvider
          identityProviderId
        }
      }
    `;

    const result = client.mutate<
      CreateAndLinkSecondaryAuthMutation,
      CreateAndLinkSecondaryAuthMutationVariables
    >({
      mutation: MUTATION,
      variables: { email, username, password },
    });

    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function getUserAgreements(
  client: ApolloClient<object>
): Promise<GetUserAgreementsQuery["userAgreements"]> {
  return new Promise((resolve, reject) => {
    const GET_USER_AGREEMENTS = gql`
      query getUserAgreements {
        userAgreements {
          hash
          type
          agreementDate
        }
      }
    `;
    const result = client.query<
      GetUserAgreementsQuery,
      GetUserAgreementsQueryVariables
    >({
      query: GET_USER_AGREEMENTS,
      variables: {},
    });

    result.then(
      (response) => {
        resolve(response.data.userAgreements);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function createAuthLinkingSession(
  client: ApolloClient<object>
): Promise<CreateAuthLinkingSessionMutation> {
  return new Promise((resolve, reject) => {
    const CREATE_AUTH_LINKING_SESSION = gql`
      mutation createAuthLinkingSession {
        createAuthLinkingSession {
          id
          userId
          status
        }
      }
    `;

    const result = client.mutate<
      CreateAuthLinkingSessionMutation,
      CreateAuthLinkingSessionMutationVariables
    >({
      mutation: CREATE_AUTH_LINKING_SESSION,
      variables: {},
    });

    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function linkPaychexRolloverUser(
  client: ApolloClient<object>
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const LINK_PAYCHEX_ROLLOVER_USER = gql`
      mutation linkPaychexRolloverUser {
        linkPaychexRolloverUser
      }
    `;
    const result = client.mutate<{ linkPaychexRolloverUser: boolean }>({
      mutation: LINK_PAYCHEX_ROLLOVER_USER,
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.linkPaychexRolloverUser);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function linkPaychexPerksUser(client: ApolloClient<object>): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const LINK_PAYCHEX_PERKS_USER = gql`
      mutation linkPaychexPerksUser {
        linkPaychexPerksUser
      }
    `;
    const result = client.mutate<{ linkPaychexPerksUser: boolean }>({
      mutation: LINK_PAYCHEX_PERKS_USER,
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.linkPaychexPerksUser);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

// Paychex Flex (Perks): fetch the worker/employment records for the signed-in
// person. Backend-pending — see PAYCHEX_PERKS_BACKEND_CONTRACT.md.
function getPaychexWorkerRecords(
  client: ApolloClient<object>
): Promise<PaychexWorkerRecord[]> {
  return new Promise((resolve, reject) => {
    const GET_WORKER_RECORDS = gql`
      query getPaychexWorkerRecords {
        getPaychexWorkerRecords {
          id
          companyId
          employerName
          displayId
          payrollFrequency
          status
          workerType
        }
      }
    `;
    const result = client.query<{
      getPaychexWorkerRecords: PaychexWorkerRecord[];
    }>({
      query: GET_WORKER_RECORDS,
      fetchPolicy: "network-only",
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getPaychexWorkerRecords);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

// Paychex Flex (Perks): fetch a single worker record after the person selects
// it from the list. Unlike the list query, this resolves `payrollFrequency`
// (from Paychex pay-standards) and `companyId` — both needed for the schedule.
// Backend-pending — see PAYCHEX_PERKS_BACKEND_CONTRACT.md.
function getPaychexWorkerRecord(
  client: ApolloClient<object>,
  workerId: string
): Promise<PaychexWorkerRecord> {
  return new Promise((resolve, reject) => {
    const GET_WORKER_RECORD = gql`
      query getPaychexWorkerRecord($workerId: ID!) {
        getPaychexWorkerRecord(workerId: $workerId) {
          id
          companyId
          employerName
          displayId
          payrollFrequency
          status
          workerType
        }
      }
    `;
    const result = client.query<{
      getPaychexWorkerRecord: PaychexWorkerRecord;
    }>({
      query: GET_WORKER_RECORD,
      variables: { workerId },
      fetchPolicy: "network-only",
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getPaychexWorkerRecord);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

// Paychex Flex (Perks): the signed-in person's active PAYCHECK_SPLIT
// contribution (v1: at most one), merged with its schedule so companyId /
// frequency / nextScheduledDate / scheduleId are populated — or null when none
// is set up. Mirrors how recurringContribution is read.
function getPaycheckSplitContribution(
  client: ApolloClient<object>
): Promise<PaychexSplitContribution | null> {
  return new Promise((resolve, reject) => {
    const GET_SPLIT = gql`
      query getPaycheckSplitContribution {
        getPaycheckSplitContribution {
          id
          amount
          accountId
          companyId
          frequency
          nextScheduledDate
          scheduleId
        }
      }
    `;
    const result = client.query<{
      getPaycheckSplitContribution: PaychexSplitContribution | null;
    }>({
      query: GET_SPLIT,
      fetchPolicy: "network-only",
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getPaycheckSplitContribution);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export const userService = {
  register,
  registerAdmin,
  getUserProfile,
  updateUserProfile,
  validateEin,
  getState,
  logout,
  confirmEmail,
  getIraAccountForm,
  updateIraAccountIdentity,
  updateIraAccountUserCitizenship,
  updateIraAccountTrustedContact,
  updateIraAccountEmploymentStatus,
  updateIraAccountEmploymentPosition,
  updateIraAccountDisclosure,
  updateIraAccountInvestmentProfile,
  updateIraAccountSuitability,
  updateIraAccountTermsAndSubmit,
  updateEmployerAccountTermsAndSubmit,
  getValidatedAddress,
  userCompletedKyc,
  userCompletedOnboarding,
  getNeededYellowPathDocs,
  updateIraYellowPathDocuments,
  closeUserAccount,
  requestEmailChange,
  getEmailChangeRequest,
  deleteEmailChangeRequest,
  verifyEmailChangeRequest,
  updateInvestmentProfileAndSuitability,
  getInvestmentProfileAndSuitability,
  getInvestmentProfile,
  acceptInvitation,
  getEmployerLinkRequests,
  getLinkedEmployers,
  linkPaychexPerksUser,
  linkPaychexRolloverUser,
  getPaychexWorkerRecords,
  getPaychexWorkerRecord,
  getPaycheckSplitContribution,
  deleteEmployerLink,
  startInvestmentProfileUpdateRequest,
  startPortfolioUpdateRequest,
  exitInvestmentProfileUpdateRequest,
  deleteEmployerLinkRequest,
  skipSsn,
  submitSsn,
  submitEin,
  createSupportRequest,
  optOut,
  getRequiredStepsRequest,
  updateRequiredDisclosuresRequest,
  updateSelectedAccountType,
  initializeRollover,
  initializePaychexRollover,
  submitRolloverAccountInfo,
  submitUserRolloverInfo,
  addRolloverAccountDisclosure,
  updateRolloverInvestmentProfileAndSuitability,
  updateRolloverAccountTermsAndSubmit,
  updatePaychexAccountTermsAndSubmit,
  userTermsDate,
  linkSecondaryAuth,
  getRolloverInfo,
  cancelRollover,
  registerIndividualUser,
  createAndLinkSecondaryAuth,
  getUserAgreements,
  closeAndLiquidateAccountWithDisbursement,
  closeAndLiquidateAccount,
  createAuthLinkingSession,
};
